import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useExternalGrants } from "@/components/organisms/ExternalGrants/ExternalGrants.hooks";
import { PendingInvitationsList } from "@/components/organisms/Invitations/InvitationsPendingList";
import { usePendingInvitations } from "@/components/organisms/Invitations/Invitations.hooks";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";
import { Card } from "@/components/ui/card";

export function InvitationsView() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const highlighted =
    searchParams.get("account") ?? searchParams.get("external_grant");
  const { pendingInvitations, loading, refetch } = usePendingInvitations();
  const {
    asked: pendingGrants,
    isLoading: isLoadingGrants,
    refetch: refetchGrants,
  } = useExternalGrants();
  const isLoading = loading || isLoadingGrants;

  // Opened at an invitation that is none of theirs - sent to another address
  // than the one they are signed in with, asked of a firm they do not own, or
  // answered already - it is on its own page that they are told which, and
  // switched over. Asked once, as the page opens: one answered here since is
  // theirs no more, and they stay.
  const hasLookedForOpened = useRef(false);
  useEffect(() => {
    if (isLoading || hasLookedForOpened.current) return;
    hasLookedForOpened.current = true;
    const account = searchParams.get("account");
    const grant = searchParams.get("external_grant");
    if (
      account &&
      !pendingInvitations.some(({ identifier }) => identifier === account)
    ) {
      navigate(`/invitation/business/${account}`, { replace: true });
    } else if (
      grant &&
      !pendingGrants.some(({ identifier }) => identifier === grant)
    ) {
      navigate(`/invitation/books/${grant}`, { replace: true });
    }
  }, [isLoading, navigate, pendingGrants, pendingInvitations, searchParams]);

  return (
    <div className="w-full max-w-180 ml-auto mr-auto p-6 flex flex-col gap-4">
      <div>
        <Typography level="h3">Invitations</Typography>
        <Typography level="body-md" className="text-muted-foreground/80">
          Accept invitations to join organizations on Neoledger, and books
          shared with you as an accountant.
        </Typography>
      </div>

      {isLoading ? (
        <Card className="p-4">
          <Typography level="title-sm">
            <Skeleton variant="inline">Business name</Skeleton>
          </Typography>
          <Typography level="body-xs">
            <Skeleton variant="inline">
              You have been invited to join this organization
            </Skeleton>
          </Typography>
        </Card>
      ) : pendingInvitations.length === 0 && pendingGrants.length === 0 ? (
        <Typography level="body-md">
          You have no pending invitations.
        </Typography>
      ) : (
        <PendingInvitationsList
          invitations={pendingInvitations}
          externalGrants={pendingGrants}
          highlightedAccountIdentifier={highlighted}
          onChanged={() => {
            void refetch();
            void refetchGrants();
          }}
        />
      )}
    </div>
  );
}
